import { WebClientListResourceFetcher } from '~commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { LongRunningAction } from '~permissions/services/LongRunningAction';
import { ILongRunningAction } from '~permissions/services/types/ILongRunningAction';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import {UpdateAnalyticsExtSiteSettingsRequest} from '~src/ts/ServerApi/requests/settings/extensions/analytics/UpdateAnalyticsExtSiteSettingsRequest';
import {UpdateExtsvcServerSettingsRequest} from '~src/ts/ServerApi/requests/settings/extensions/analytics/UpdateExtsvcServerSettingsRequest';
import {
  GetPrepExtensionsServerSettingsRequest,
  IGetPrepExtensionsServerSettingsResult
} from '~src/ts/ServerApi/requests/settings/extensions/prep/GetPrepExtensionsServerSettingsRequest';
import {
  GetPrepExtensionsSiteSettingsRequest,
  IGetPrepExtensionsSiteSettingsResult
} from '~src/ts/ServerApi/requests/settings/extensions/prep/GetPrepExtensionsSiteSettingsRequest';
import {
  IUpdatePrepExtensionsSiteSettingsParams,
  UpdatePrepExtensionsSiteSettingsRequest
} from '~src/ts/ServerApi/requests/settings/extensions/prep/UpdatePrepExtensionsSiteSettingsRequest';

import { Messages } from '../../../../codegen/Localize';
import { ByteSizeConversionFactor, SizeFilter } from '../../../../commonInfra/utils/SizeFilter';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { RequestHelper } from '../../../../httpClient/utils/RequestHelper';
import { ResultHelper } from '../../../../httpClient/utils/ResultHelper';
import {
  WebHttpClientWithErrorToastInstance
} from '../../../../httpClient/WebHttpClientWithErrorToast';
import { getSessionManagementServiceInstance } from '../../../../sessionManagement/SessionManagementService';
import { AskDataMode } from '../../../../settings/types/AskDataMode';
import { IServerAdminSettings, ISiteSettingsForServerAdmin } from '../../../../settings/types/ISite';
import {
  ExtensionSafeListEntry, IAnalyticsExtSiteSettings,
  IDashExtSiteSettings,
  IPrepExtensionsSiteSettings,
  ISiteAdminBridgeSettings,
  ISiteAdminSettings,
  ISiteBridgeSettingsForSiteAdmin,
  ISiteSettingsForSiteAdmin,
  ISiteSubscriptionSettings,
  UserQuotaMode,
  UserVisibilityMode
} from '../../../../settings/types/ISiteSettingsForSiteAdmin';
import { ExtractEncryptionMode, SiteAvailabilityState } from '../../../../sites/SitePropertyTypes';
import { SiteSettingsErrorCode } from '../../../helpers/ErrorCodes';
import { Resource } from '../../../resources/Resource';
import { CreateSiteRequest, ICreateSiteParams, ICreateSiteResult } from '../../../ServerApi/requests/CreateSiteRequest';
import { DeleteSitesRequest } from '../../../ServerApi/requests/DeleteSitesRequest';
import { GetSiteBridgeSettingsForSiteAdminRequest } from '../../../ServerApi/requests/GetSiteBridgeSettingsForSiteAdminRequest';
import {
  GetSiteSettingsForServerAdminRequest,
  IGetSiteSettingsForServerAdminResult
} from '../../../ServerApi/requests/GetSiteSettingsForServerAdminRequest';
import {
  GetSiteSettingsForSiteAdminRequest,
  IGetSiteSettingsForSiteAdminParams,
  IGetSiteSettingsForSiteAdminResult
} from '../../../ServerApi/requests/GetSiteSettingsForSiteAdminRequest';
import { GetSitesRequest, IGetSitesResult } from '../../../ServerApi/requests/GetSitesRequest';
import {
  GetDashExtSiteSettingsRequest,
  IGetDashExtSiteSettingsResult
} from '../../../ServerApi/requests/settings/extensions/dashboard/GetExtensionsSiteSettingsRequest';
import {
  IUpdateDashExtSiteSettingsParams,
  UpdateExtensionsSiteSettingsRequest
} from '../../../ServerApi/requests/settings/extensions/dashboard/UpdateExtensionsSiteSettingsRequest';
import { UpdateSiteAvailabilityRequest } from '../../../ServerApi/requests/UpdateSiteAvailabilityRequest';
import {
  IUpdateSiteBridgeSettingsForSiteAdminParams,
  UpdateSiteBridgeSettingsForSiteAdminRequest
} from '../../../ServerApi/requests/UpdateSiteBridgeSettingsForSiteAdminRequest';
import {
  IUpdateSiteSettingsForServerAdminParams,
  UpdateSiteSettingsForServerAdminRequest
} from '../../../ServerApi/requests/UpdateSiteSettingsForServerAdminRequest';
import {
  IUpdateSiteSettingsForSiteAdminParams,
  UpdateSiteSettingsForSiteAdminRequest
} from '../../../ServerApi/requests/UpdateSiteSettingsForSiteAdminRequest';

export const defaultSubscriptionSettingsForSite: ISiteSubscriptionSettings = {
  subscriptionsEnabled: true,
  subscribeOthersEnabled: true,
  customScheduleSubscriptionForSiteEnabled: false,
  allowSubscriptionAttachments: true,
  serverAllowSubscriptionAttachments: true
};

// Used in the Create Site flow
export const defaultSiteSettingsForServerAdmin: ISiteSettingsForServerAdmin = {
  contentType: 'site',
  id: undefined,
  userCount: 1,
  licensedUserCount: 1,
  adminCount: undefined,
  creatorCount: 0,
  explorerCount: 0,
  viewerCount: 0,
  availability: SiteAvailabilityState.ACTIVE,
  currentLoggedInUserCount: undefined,
  storageQuotaPercentUsed: undefined,
  storageUsedBytes: undefined,
  createdAt: undefined,
  settings: {
    urlName: '',
    name: '',
    autoSuspendRefreshEnabled: true,
    autoSuspendRefreshInactivityWindow: 32,
    storageQuotaEnabled: false,
    storageQuotaBytes: undefined,
    siteAdminsCanAddUsers: true,
    userQuotaMode: UserQuotaMode.SERVER_LIMIT,
    userQuota: null,
    metricsEnabled: false,
    managedKeychainCountEnabled: false,
    cacheWarmupEnabled: true,
    authoringEnabled: true,
    webEditingEnabled: true,
    editingFlowsEnabled: true,
    schedulingFlowsEnabled: true,
    linkedTaskSchedulingEnabled: FeatureFlags.LinkedTaskSchedulingEnabled,
    linkedTaskRunNowEnabled: FeatureFlags.LinkedTaskSchedulingEnabled,
    catalogingOnSiteEnabled: true,
    flowsOnSiteEnabled: true,
    versionHistoryEnabled: true,
    contentVersionLimitEnabled: true,
    contentVersionLimit: 25,
    guestAccessEnabled: false,
    customSubscriptionEmailEnabled: false,
    customSubscriptionEmail: '',
    customSubscriptionFooterEnabled: false,
    customSubscriptionFooter: '',
    defaultStartPage: undefined,
    runNowEnabled: undefined,
    tierCreatorCapacity: null,
    tierExplorerCapacity: null,
    tierViewerCapacity: null,
  },
  subscriptionSettings: defaultSubscriptionSettingsForSite,
};

export const defaultSiteSettings: ISiteAdminSettings = {
  derivedPermissionsEnabled: false,
  refreshTokenEnabled: true,
  notificationEnabled: true,
  addSupportUserEnabled: false,
  remoteQueryingEnabled: false,
  sheetImageEnabled: true,
  siteInviteEmailEnabled: true,
  extractEncryptionMode: ExtractEncryptionMode.DISABLED,
  isAdminDataSourceEnabled: false,
  siteStartPageUri: '',
  siteNamedSharingSettings: {
    namedSharingEnabled: true
  },
  vizRecommendationsEnabled: true,
  vizRecUsernameVisibilityEnabled: true,
  sitePersonalSpaceSettings: {
    personalSpaceEnabled: true,
    personalSpaceStorageQuotaSet: true,
    personalSpaceStorageQuota: 2048 * ByteSizeConversionFactor.ONE_MB
  },
  alternativeFederationEngineEnabled: true,
  askDataMode: AskDataMode.ENABLED_BY_DEFAULT,
  userVisibility: UserVisibilityMode.FULL,
  explainDataEnabled: true,
  requestAccessEnabled: true,
  dataAlertsEnabled: true,
  siteCommentingSettings: {
      commentingEnabled: true,
      commentingMentionsEnabled: true
  },
  metricsContentTypeEnabled: true,
  tagLimit: 50,
  webZoneContentEnabled: true,
  notifySiteAdminsOnThrottleEnabled: true,
  runNowEnabled: null,
  catalogObfuscationEnabled: true,
  timeZone: '',
  useDefaultTimeZone: true,
  publicCollectionsEnabled: true,
  einsteinInFlowEnabled: true,
  canOwnerAccelerate: true,
  dqwSubscriptionsEnabled: FeatureFlags.dqwsubscriptionEmailsEnabled,
};

export const defaultSiteSettingsForSiteAdmin: ISiteSettingsForSiteAdmin = {
  siteId: undefined,
  siteAdminSettings: defaultSiteSettings,
  subscriptionSettings: defaultSubscriptionSettingsForSite,
};

export const defaultUrl: string = 'https://';
export const defaultEntry: ExtensionSafeListEntry = {
  'url': defaultUrl,
  'fullDataAllowed': false,
  'promptNeeded': true
};

export const defaultExtensionsSiteSettings: IDashExtSiteSettings = {
  extensionsEnabled: true,
  allowSandboxed: true,
  safeList: []
};


export class Sites extends Resource<ISiteSettingsForServerAdmin> {

  public static readonly instance = new Sites(new WebClientListResourceFetcher(GetSitesRequest, (result: IGetSitesResult) => {
    return result.sites.map((site) => {
      return {
        ...site,
        contentType: 'site'
      } as ISiteSettingsForServerAdmin;
    });
  }));

  public getSiteSettingsForServerAdminById(id: string): Promise<IGetSiteSettingsForServerAdminResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSiteSettingsForServerAdminRequest({ id }));
  }

  public getSiteSettingsForSiteAdminById(id: string): Promise<IGetSiteSettingsForSiteAdminResult> {
    const params: IGetSiteSettingsForSiteAdminParams = {
      id: id
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSiteSettingsForSiteAdminRequest(params));
  }

  public getSiteBridgeSettingsForSiteAdminById(id: string): Promise<ISiteBridgeSettingsForSiteAdmin> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetSiteBridgeSettingsForSiteAdminRequest({ id }));
  }

  public getCurrentExtensionsSiteSettingsForSiteAdmin(): Promise<IGetDashExtSiteSettingsResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest(new GetDashExtSiteSettingsRequest({path: ''}));
  }

  public getCurrentPrepExtensionsSiteSettingsForSiteAdmin(): Promise<IGetPrepExtensionsSiteSettingsResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest((new GetPrepExtensionsSiteSettingsRequest({path: ''})));
  }

  public getCurrentPrepExtensionsServerSettings(): Promise<IGetPrepExtensionsServerSettingsResult> {
    return WebHttpClientWithErrorToastInstance.sendRequest((new GetPrepExtensionsServerSettingsRequest({})));
  }

  public getCurrentSiteSettingsForServerAdmin(): Promise<ISiteSettingsForServerAdmin> {
    return this.getSiteSettingsForServerAdminById(getSessionManagementServiceInstance().sessionSiteId());
  }

  public getCurrentSiteSettingsForSiteAdmin(): Promise<IGetSiteSettingsForSiteAdminResult> {
    return this.getSiteSettingsForSiteAdminById(getSessionManagementServiceInstance().sessionSiteId());
  }

  public getCurrentSiteBridgeSettingsForSiteAdmin(): Promise<ISiteBridgeSettingsForSiteAdmin> {
    return this.getSiteBridgeSettingsForSiteAdminById(getSessionManagementServiceInstance().sessionSiteId());
  }

  public updateSiteAvailability(ids: string[], availability: string): Promise<IResult> {
    return WebHttpClientWithErrorToastInstance.sendRequests(
      RequestHelper.buildRequests(ids, (idsBlock) => new UpdateSiteAvailabilityRequest({
        ids: idsBlock,
        availability: availability
      })),
      ResultHelper.combineResults);
  }

  public suspend(ids: string[]): Promise<IResult> {
    return this.updateSiteAvailability(ids, SiteAvailabilityState.SUSPENDED);
  }

  public activate(ids: string[]): Promise<IResult> {
    return this.updateSiteAvailability(ids, SiteAvailabilityState.ACTIVE);
  }

  public updateSiteSettingsForServerAdmin(id: string, settings: IServerAdminSettings): Promise<IResult> {
    const params: IUpdateSiteSettingsForServerAdminParams = {
      id: id,
      settings: settings
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSiteSettingsForServerAdminRequest(params));
  }

  public updateSiteSettingsForSiteAdmin(id: string, siteAdminSettings: ISiteAdminSettings, subscriptionSettings?: ISiteSubscriptionSettings): Promise<IResult> {
      const params: IUpdateSiteSettingsForSiteAdminParams = {
          id: id,
          siteAdminSettings: siteAdminSettings
      };
      if (subscriptionSettings) {
        params.subscriptionSettings = subscriptionSettings;
      }

      return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSiteSettingsForSiteAdminRequest(params));
  }

  public updateSiteBridgeSettingsForSiteAdmin(id: string, siteAdminBridgeSettings: ISiteAdminBridgeSettings): Promise<IResult> {
      const params: IUpdateSiteBridgeSettingsForSiteAdminParams = {
          id: id,
          siteAdminBridgeSettings: siteAdminBridgeSettings
      };

      return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateSiteBridgeSettingsForSiteAdminRequest(params));
  }

  public updateCurrentExtensionsSiteSettingsForSiteAdmin(extensionSettings: IDashExtSiteSettings): Promise<IResult> {
    const params: IUpdateDashExtSiteSettingsParams = {
      siteSettings: extensionSettings
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdateExtensionsSiteSettingsRequest(params));
  }

  public updateCurrentPrepExtensionsSiteSettingsForSiteAdmin(prepExtensionSettings: IPrepExtensionsSiteSettings): Promise<IResult> {
    const params: IUpdatePrepExtensionsSiteSettingsParams = {
      prepExtensionsSiteSettings: prepExtensionSettings
    };
    return WebHttpClientWithErrorToastInstance.sendRequest(new UpdatePrepExtensionsSiteSettingsRequest(params));
  }

  public updateAnalyticsExtensionsSiteSettings(analyticsExtensionsSiteSettingsEnabled: IAnalyticsExtSiteSettings): Promise<IResult> {
    if (getSessionManagementServiceInstance().isSingleSiteOnPremServer()) {
      let serverSettings = {enabled: analyticsExtensionsSiteSettingsEnabled.enabled};
      return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateExtsvcServerSettingsRequest(serverSettings))
        .then(() => {
          return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtSiteSettingsRequest(analyticsExtensionsSiteSettingsEnabled));
        });
    } else {
      return WebHttpClientWithErrorToastInstance.sendTagRequest(new UpdateAnalyticsExtSiteSettingsRequest(analyticsExtensionsSiteSettingsEnabled));
    }
  }

  public createSite(settings: IServerAdminSettings, siteAdminSettings?: ISiteAdminSettings, subscriptionSettings?: ISiteSubscriptionSettings): Promise<void> {
    const params: ICreateSiteParams = {
      settings: settings
    };

    if (siteAdminSettings) {
      params.siteAdminSettings = siteAdminSettings;
    }

    if (subscriptionSettings) {
      params.subscriptionSettings = subscriptionSettings;
    }
    return WebHttpClientWithErrorToastInstance.sendRequestAndDelay(new CreateSiteRequest(params))
      .then<void>((result: ICreateSiteResult) => {
        if (result.errors) {
          return Promise.reject(result.errors);
        }
      });
  }

  public del(ids: string[], notifyCallback?: (result: ILongRunningAction) => void): Promise<any> {
    // Note: We don't send multiple "delete sites" requests because the result is a long running action,
    // and tracking more than one long running action at once would be complicated.
    // Since we don't, attempts to delete more than the server-allowed maximum will fail.
    // This isn't so bad, because the maximum is 1000, and that's a *lot* of sites
    // to try to delete all at once.  It's likely the server can't handle that anyway.
    return WebHttpClientWithErrorToastInstance.sendRequest(new DeleteSitesRequest({ ids: ids }))
      .then(LongRunningAction.getActionIdFromActionResult)
      .then((actionId: string) => LongRunningAction.trackAsyncJobProgressWithPromise(actionId, notifyCallback));
  }

  public errorMessage(code: SiteSettingsErrorCode, site: ISiteSettingsForServerAdmin): string {
    switch (code) {
      case SiteSettingsErrorCode.SiteNotFound:
        return Messages.siteSettingsFailedSiteNotFound();
      case SiteSettingsErrorCode.SiteNameIsEmptyOrNull:
        return Messages.siteSettingsFailedSiteNameIsEmptyOrNull();
      case SiteSettingsErrorCode.SiteAdminCannotAddUsersButUserQuotaIsSet:
        return Messages.siteSettingsFailedSiteAdminCannotAddUsersButUserQuotaIsSet();
      case SiteSettingsErrorCode.InvalidUserQuotaOrStorageQuota:
        return Messages.siteSettingsFailedInvalidUserQuotaOrStorageQuota();
      case SiteSettingsErrorCode.UserQuotaGreaterThanServerLicenseLimit:
        return Messages.siteSettingsFailedUserQuotaGreaterThanServerLicenseLimit();
      case SiteSettingsErrorCode.InvalidCustomEmailAddress:
        return Messages.siteSettingsFailedInvalidCustomEmailAddress();
      case SiteSettingsErrorCode.SiteNameAlreadyExists:
        return Messages.siteSettingsFailedSiteNameOrUrlNameAlreadyExists();
      case SiteSettingsErrorCode.SiteUrlNameAlreadyExists:
        return Messages.siteSettingsFailedSiteNameOrUrlNameAlreadyExists();
      case SiteSettingsErrorCode.SiteSubscriptionsSetWithDisabledServerSubscriptions:
        return Messages.siteSettingsFailedSiteSubscriptionsSetWithDisabledServerSubscriptions();
      case SiteSettingsErrorCode.AttemptedToUpdateDefaultSiteUrlName:
        return Messages.siteSettingsFailedAttemptedToUpdateDefaultSiteUrlName();
      case SiteSettingsErrorCode.AttemptedToSuspendDefaultSite:
        return Messages.siteSettingsFailedAttemptedToSuspendDefaultSite();
      case SiteSettingsErrorCode.SiteNameTooLong:
        return Messages.siteSettingsFailedSiteNameTooLong();
      case SiteSettingsErrorCode.SiteUrlNameTooLong:
        return Messages.siteSettingsFailedSiteUrlNameTooLong();
      case SiteSettingsErrorCode.StorageQuotaLessThanStorageUsed: // server admin - requires 'site' parameter
        return Messages.siteSettingsFailedStorageQuotaLessThanStorageUsed({
          STORAGE_USED : SizeFilter(site.storageUsedBytes)
        });
      case SiteSettingsErrorCode.SupportUsersOwnContent:
        return Messages.siteSettingsFailedSupportUsersOwnContent();
      default:
        return Messages.settingsUpdateFailure();
    }
  }
}
